import type { PricePoint } from "@/lib/data/types";

/**
 * Prozentuale Kursveränderung vom ältesten verfügbaren Kurs innerhalb des
 * `lookbackDays`-Fensters bis zum letzten verfügbaren Kurs - kein exaktes
 * Kalendertag-Matching (Handelstage/Feiertage variieren je Börse). Fällt bei kürzerer
 * Historie (z.B. junger Börsengang) ehrlich auf den ältesten tatsächlich verfügbaren
 * Kurs zurück, statt undefined zu liefern oder einen Wert zu erfinden - realisiert dann
 * schlicht eine kürzere Periode als das Lookback-Fenster. Liefert undefined nur, wenn
 * weniger als 2 gültige Kurspunkte vorliegen. Reine Funktion, kein I/O.
 */
export function computePriceReturn(
  priceHistory: PricePoint[],
  lookbackDays: number,
  now: Date = new Date(),
): number | undefined {
  const sorted = [...priceHistory].filter((p) => p.close > 0).sort((a, b) => a.date.localeCompare(b.date));
  if (sorted.length < 2) return undefined;

  const cutoff = new Date(now.getTime() - lookbackDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const base = sorted.find((p) => p.date >= cutoff) ?? sorted[0];
  const latest = sorted[sorted.length - 1];
  if (base === latest || base.close <= 0) return undefined;

  return (latest.close - base.close) / base.close;
}

/** Standard-Zeitraeume fuer den Sektor-/Index-Heatmap-Umschalter (backlog.md -> "Sektor-
 * /Index-Heatmap: Zeitraum-Umschalter") - "1 Tag bis mehrere Jahre wählbar, analog
 * Aktienfinders Maps". Reihenfolge = Anzeigereihenfolge in der UI. */
export const PRICE_RETURN_PERIODS: readonly { key: PriceReturnPeriodKey; label: string }[] = [
  { key: "1t", label: "1T" },
  { key: "1w", label: "1W" },
  { key: "1m", label: "1M" },
  { key: "3m", label: "3M" },
  { key: "6m", label: "6M" },
  { key: "ytd", label: "YTD" },
  { key: "1y", label: "1J" },
  { key: "3y", label: "3J" },
  { key: "5y", label: "5J" },
] as const;

export type PriceReturnPeriodKey = "1t" | "1w" | "1m" | "3m" | "6m" | "ytd" | "1y" | "3y" | "5y";

const FIXED_LOOKBACK_DAYS: Record<Exclude<PriceReturnPeriodKey, "ytd">, number> = {
  "1t": 1,
  "1w": 7,
  "1m": 30,
  "3m": 91,
  "6m": 182,
  "1y": 365,
  "3y": 365 * 3,
  "5y": 365 * 5,
};

function daysSinceYearStart(now: Date): number {
  const yearStart = Date.UTC(now.getUTCFullYear(), 0, 1);
  return Math.max(1, Math.ceil((now.getTime() - yearStart) / (24 * 60 * 60 * 1000)));
}

/**
 * Berechnet alle Standard-Zeiträume (`PRICE_RETURN_PERIODS`) auf einmal über dieselbe
 * bereits geladene Kurshistorie - kein Zusatz-Request pro Periode, `computePriceReturn()`
 * ist eine reine Funktion und die Kurshistorie wird ohnehin schon für `computeScore()`
 * geladen (siehe lib/data/score-summary.ts). "ytd" misst ab dem 1. Januar des `now`-Jahres
 * statt eines festen Lookback-Fensters.
 */
export function computeAllPriceReturns(
  priceHistory: PricePoint[],
  now: Date = new Date(),
): Record<PriceReturnPeriodKey, number | undefined> {
  const result = {} as Record<PriceReturnPeriodKey, number | undefined>;
  for (const { key } of PRICE_RETURN_PERIODS) {
    const lookbackDays = key === "ytd" ? daysSinceYearStart(now) : FIXED_LOOKBACK_DAYS[key];
    result[key] = computePriceReturn(priceHistory, lookbackDays, now);
  }
  return result;
}
